import mori from 'mori';
import getDatacenterDistributions from '../services/distributionFilters';
import { appDefault } from '../config/config';
import { kffIsNumber, kffNumericValue } from '../util/util';
import slugify from '../util/slugify';

export default function(data) {
  // @TODO make county data processor
  // the easiest way is probably to do some conditional processing of these
  // medicare json files in place of the other function assignments in
  // buildSpreadsheetData. The json is ~close to what you need, but will
  // require some massaging.

  /**
   * Lets format sheetData into a wonderful style of:
   * mori.vector(
   *   mori.hashMap(
   *     'year', 0000,
   *     'location', XXXX
   *     'value', 000000000
   *   )
   * )
   */
  const timeframes = mori.toClj(data.data.timeframes);

  const immutableHeaders = getHeaders(mori.first(timeframes));
  const { immutableDistributions } =
    getDatacenterDistributions(immutableHeaders);
  const sheetData = mori.flatten(
    mori.map(
      (parent) => mori.map(
        (row) => mori.hashMap(
          'year', mori.get(parent, 'timeframe_title'),
          'label', ((mori.first(row).toString() === 'Dist. of Columbia') ?
            'District of Columbia' :
            mori.first(row).toString()),
          'values', mori.zipmap(
            mori.map(
              (item) => mori.set([
                mori.get(item, 'characteristic'),
                mori.get(item, 'type'),
              ]),
              mori.sort(
                (a, b) => mori.get(a, 'idx') - mori.get(b, 'idx'),
                mori.rest(immutableHeaders)
              )
            ),
            mori.map(
              (item) => kffIsNumber(item) ? kffNumericValue(item) : item,
              mori.rest(row)
            )
          )
        ),
        mori.drop(2, mori.get(parent, 'grid'))
      ),
      timeframes
    )
  );

  /**
   * Flatten out the sheet definitions so we can pluck them later...
   */
  const definitions = buildGeoDefinitions(
    sheetData,
    mori.toClj(data.geoDefinitions)
  );

  const flatData = mori.groupBy(
    (item) => mori.get(item, 'year'),
    mori.filter(
      // need to check to remove any data sets without definitions
      (item) => !(!mori.hasKey(item, 'header') &&
        /\d/.test(mori.get(item, 'label'))),
      mori.map(
        (item) => mori.merge(
          item,
          mori.get(definitions, mori.get(item, 'label'))
        ),
        sheetData
      )
    )
  );

  const immutableData = buildData(flatData);
  const columns = buildColumnData(flatData);
  const config = mori.hashMap(
    'notesSources', mori.hashMap(
      'Notes', mori.set([
        mori.hashMap(
          'Data Format', null,
          'Distribution', null,
          'Geography', null,
          'Note', data.data.notes,
          'Timeframe', 'any',
          'Type', 'Notes'
        ),
      ]),
      'Sources', mori.set([
        mori.hashMap(
          'Data Format', null,
          'Distribution', null,
          'Geography', null,
          'Note', data.data.source,
          'Timeframe', 'any',
          'Type', 'Sources'
        ),
      ]),
      'Definitions', mori.set([
        mori.hashMap(
          'Data Format', null,
          'Distribution', null,
          'Geography', null,
          'Note', data.data.definitions,
          'Timeframe', 'any',
          'Type', 'Definitions'
        ),
      ])
    ),
    'globalRules', mori.hashMap(
      'medicare', true,
      'Locked', false,
      'Graph', false,
      'Map', true
    ),
    'settings', mori.hashMap(
      'globalSettings', mori.hashMap(
        'Precision', '1',
        'hasDataviews', false
      )
    )
  );

  return {
    config,
    immutableHeaders,
    immutableData,
    immutableDataviews: [{
      'label': 'default',
      'type': 'default',
    }],
    immutableDistributions,
    columns: columns,
    timeframes: mori.intoArray(mori.map(
      (item) => mori.get(item, 'timeframe_title').toString(),
      timeframes
    )),
    globalRules: mori.toJs(mori.get(config, 'globalRules')),
    globalSettings: mori.toJs(
      mori.getIn(config, ['settings', 'globalSettings'])
    ),
    distributionCount: mori.count(immutableDistributions),
  };
  // return data;
}

/**
 * Build the list of geodefinitions to use
 * @param  {mori func} sheetData      sheet data (parse labels out of it)
 * @param  {mori func} geoDefinitions geo definition hashmap
 * @return {mori func}                map of geo definitions which are valid
 */
function buildGeoDefinitions(sheetData, geoDefinitions) {
  return mori.reduce(
    (acc, row) => mori.conj(
      acc,
      reduceDefinitions(
        row,
        mori.map(
          (next) => mori.get(next, 'label'),
          sheetData
        )
      )
    ),
    mori.hashMap(),
    geoDefinitions
  );
}

/**
 * Build the column data
 * @param  {mori func} flatData     datasheet
 * @return { nested, wrapups}       nested and wrapups
 */
function buildColumnData(flatData) {
  const nested = mori.toJs(mori.reduce(
    (acc, item) => mori.conj(
      acc,
      (['Counties', 'MSAs'].indexOf(mori.first(item)) < 0 ?
        mori.hashMap(
          slugify(mori.first(item)),
          mori.hashMap(
            'label', mori.first(item),
            'children', mori.reduce(
              (acc1, next) => mori.conj(
                acc1,
                mori.hashMap(
                  slugify(mori.get(next, 'label')),
                  mori.hashMap('label', mori.get(next, 'label'))
                )
              ),
              mori.hashMap(),
              mori.last(item)
            )
          )
        ) :
        mori.hashMap(
          slugify(mori.first(item)),
          mori.hashMap(
            'label', mori.first(item),
            'children', mori.reduce(
              (acc1, next) => mori.conj(
                acc1,
                mori.hashMap(
                  slugify(mori.first(next)),
                  mori.hashMap(
                    'label', mori.first(next),
                    'children', mori.reduce(
                      (acc2, next1) => mori.conj(
                        acc2,
                        mori.hashMap(
                          slugify(mori.get(next1, 'label')),
                          mori.hashMap(
                            'colLabel',
                            mori.get(next1, 'label'),
                            'label',
                            (mori.first(item) !== 'Counties') ?
                              mori.get(next1, 'label') :
                              `${mori.first(next)}, ${mori.get(next1, 'label')}`
                          )
                        )
                      ),
                      mori.hashMap(),
                      mori.last(next)
                    )
                  )
                )
              ),
              mori.hashMap(),
              mori.groupBy(
                (nest) => mori.get(nest, 'state'),
                // Map data items to get list of all states in which each item exists
                mori.flatten(mori.map(
                  (item1) => mori.map(
                    (state) => mori.into(item1, mori.hashMap('state', state)),
                    mori.get(item1, 'states')
                  ),
                  // All data associated with each column section: MSA,, Counties, PDPs, etc
                  mori.last(item),
                ))
              )
            )
          )
        )
      )
    ),
    mori.hashMap(),
    mori.groupBy(
      (item) => mori.get(item, 'header') || 'States',
      mori.filter(
        (item) => appDefault.specialWrapups
          .indexOf(mori.get(item, 'label')) === -1,
        mori.first(mori.rest(mori.first(flatData)))
      )
    )
  ));

  const wrapups = mori.toJs(mori.reduce(
    (acc, next) => mori.conj(
      acc,
      mori.hashMap(
        slugify(mori.get(next, 'label')),
        mori.hashMap(
          'label', mori.get(next, 'label')
        )
      )
    ),
    mori.hashMap(),
    mori.filter(
      (item) => appDefault.specialWrapups
        .indexOf(mori.get(item, 'label')) > -1,
      mori.first(mori.rest(mori.first(flatData)))
    )
  ));

  return { nested, wrapups };
}

/**
 * Assemble the data in a way which DC expects it
 * @param  {mori obj} data    flat data sheet
 * @return {mori obj}         immutable data setup
 */
function buildData(data) {
  return mori.reduce(
    (acc, item1) => mori.conj(
      acc,
      mori.hashMap(
        mori.first(item1).toString(),
        mori.flatten(mori.map(
          (item2) => mori.map(
            (item3) => mori.into(
              mori.hasKey(item3, 'states') &&
                mori.get(item3, 'header') === 'Counties' ?
              mori.hashMap(
                mori.set(['Location', 'Geography']),
                `${
                  mori.first(mori.get(item3, 'states'))
                }, ${mori.get(item3, 'label')}`
              ) :
              mori.hashMap(
                mori.set(['Location', 'Geography']),
                mori.get(item3, 'label')
              ),
              mori.get(item3, 'values')
            ),
            item2
          ),
          mori.rest(item1)
        ))
      )
    ),
    mori.hashMap(),
    data
  );
}

/**
 * Get the headers, using only the first sheet
 * @param  {mori func} timeframe    single timeframe/datasheet
 * @return {mori func}              immutable headers map
 */
function getHeaders(timeframe) {
  return mori.reduce(
    (acc, next) => mori.conj(
      acc,
      mori.hashMap(
        'type', (mori.count(acc) === 0 ?
           'geography' :
            mori.get(
              mori.nth(mori.get(timeframe, 'grid'), 1),
              mori.count(acc)
            ).toLowerCase()
          ),
        'label', (mori.count(acc) === 0 ?
           'Geography' :
            mori.get(
              mori.nth(mori.get(timeframe, 'grid'), 1),
              mori.count(acc)
            ).toLowerCase()
          ),
        'characteristic', (next === '') ? 'Location' : next,
        'Precision', '1',
        'idx', mori.count(acc)
      )
    ),
    mori.vector(),
    mori.first(mori.get(timeframe, 'grid'))
  );
}

/**
 * Take in a definition row, and return it mapped to data
 * @param  {mori object} row              Geo Definition row
 * @param  {mori function} existingIds    IDs which exist in the dataset
 * @return {mori function}                Mapped IDs w/ Props
 */
function reduceDefinitions(row, existingIds) {
  // Check if nested
  if (
    mori.isMap(mori.first(mori.rest(mori.first(mori.first(mori.rest(row))))))
  ) {
    return mori.reduce(
      (acc, row1) => mori.conj(
        acc,
        mori.reduce(
          (acc1, item) => (
            mori.some(
              (id) => id === mori.first(item),
              existingIds
            ) ?
            mori.conj(
              acc1,
              mori.hashMap(
                mori.first(item),
                mori.hashMap(
                  'label', mori.last(item),
                  'states', mori.into(
                    mori.get(
                      mori.get(
                        acc, mori.first(item)
                      ), 'states', mori.list()
                    ),
                    mori.list(mori.first(row1))
                  ),
                  'header', mori.first(row)
                )
              )
            ) : acc1
          ),
          mori.hashMap(),
          mori.last(row1)
        )
      ),
      mori.hashMap(),
      mori.last(row)
    );
  }

  // Else do this simple remap
  return mori.reduce(
    (acc, item) => (
      mori.some(
        (id) => id === mori.first(item),
        existingIds
      ) ?
      mori.conj(
        acc,
        mori.hashMap(
          mori.first(item),
          mori.hashMap(
            'label', mori.last(item),
            'header', mori.first(row)
          )
        )
      ) : acc
    ),
    mori.hashMap(),
    mori.first(mori.rest(row))
  );
}



// WEBPACK FOOTER //
// ../~/eslint-loader!./js/reducers/countyReducer.js